import type { PhotoMissingReason, PunchPhotoStatus } from '@worklogix/shared';

import {
  countUnsyncedPhotos,
  selectEvictablePhoto,
  type PhotoRecordView,
} from '../photo/photoDrain';

import { openKioskDb, PHOTOS_STORE, promisify } from './queue';

/**
 * Durable, offline-first VERIFICATION-PHOTO queue (plan-v7), backed by the
 * Phase 2 `photos` object store in the shared kiosk IndexedDB. Kept SEPARATE
 * from the punch queue so large JPEG blobs never slow the hot punch-queue scan.
 *
 * Lifecycle: a photo is enqueued (keyed by the SAME `idempotencyKey` as its
 * punch) the instant the operator confirms a PIN — a `stored` record carries the
 * encoded blob, a `missing` record carries only its non-PII reason. The second
 * drain (`sync/syncEngine.ts`) uploads it AFTER its punch reaches the server,
 * then flips `photoSynced` + clears the blob. All ordering/quota DECISIONS live
 * in the pure `photo/photoDrain.ts`; this module is only the persistence shell.
 */

export interface QueuedPhoto {
  /** Same idempotency key as the punch this photo verifies. Primary key. */
  idempotencyKey: string;
  /** Encoded JPEG when `status === 'stored'`; null when `missing` or after sync. */
  blob: Blob | null;
  status: PunchPhotoStatus;
  /** Present only when `status === 'missing'`. */
  missingReason?: PhotoMissingReason;
  /** Client capture time (ISO-8601). */
  capturedAt: string;
  /** True once the server acknowledged the upload; blocks any re-send. */
  photoSynced: boolean;
  /** Upload attempt count, for backoff. */
  attempts: number;
}

/**
 * Soft cap on retained un-synced photos. A count guard complements the reactive
 * `QuotaExceededError` eviction: if the drain falls far behind (long offline
 * spell), we still don't grow the blob store without bound.
 */
export const MAX_UNSYNCED_PHOTOS = 200;

type Listener = (warning: string | null) => void;
const listeners = new Set<Listener>();

/** Subscribe to photo-queue soft warnings (e.g. a quota-eviction notice). */
export function subscribePhotoWarnings(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function warn(message: string | null): void {
  for (const listener of listeners) {
    listener(message);
  }
}

function store(db: IDBDatabase, mode: IDBTransactionMode): IDBObjectStore {
  return db.transaction(PHOTOS_STORE, mode).objectStore(PHOTOS_STORE);
}

/** Project the stored record to the pure drain view (drops the blob bytes). */
function toView(photo: QueuedPhoto): PhotoRecordView {
  const view: PhotoRecordView = {
    idempotencyKey: photo.idempotencyKey,
    status: photo.status,
    photoSynced: photo.photoSynced,
    capturedAt: photo.capturedAt,
    hasBlob: photo.blob !== null,
  };
  return photo.missingReason === undefined ? view : { ...view, missingReason: photo.missingReason };
}

/** All queued photos, unordered. */
export async function getAllPhotos(): Promise<QueuedPhoto[]> {
  const db = await openKioskDb();
  return promisify(store(db, 'readonly').getAll() as IDBRequest<QueuedPhoto[]>);
}

/** All queued photos projected to the pure drain view (no blob bytes). */
export async function getPhotoViews(): Promise<PhotoRecordView[]> {
  const all = await getAllPhotos();
  return all.map(toView);
}

/** Fetch one queued photo by key. */
export async function getPhoto(idempotencyKey: string): Promise<QueuedPhoto | undefined> {
  const db = await openKioskDb();
  return promisify(
    store(db, 'readonly').get(idempotencyKey) as IDBRequest<QueuedPhoto | undefined>
  );
}

/**
 * Persist a freshly-captured photo alongside its punch. Idempotent: a replayed
 * PIN reuses the SAME key, so a second enqueue for an already-uploaded photo is a
 * no-op (never re-captures/re-uploads). On `QuotaExceededError`, evict the OLDEST
 * already-uploaded photo (pure {@link selectEvictablePhoto}) and retry ONCE; if
 * still full, drop the incoming blob (record `missing`/`capture_failed`) so the
 * punch's photo slot is never left ambiguous — but NEVER block the punch.
 */
export async function enqueuePhoto(photo: QueuedPhoto): Promise<void> {
  const existing = await getPhoto(photo.idempotencyKey);
  if (existing?.photoSynced === true) {
    return; // already uploaded — do not overwrite/re-capture (idempotent replay)
  }
  try {
    await putPhoto(photo);
  } catch (error) {
    if (!isQuotaExceeded(error)) {
      throw error;
    }
    const victim = selectEvictablePhoto(await getPhotoViews());
    if (victim !== null) {
      await deletePhoto(victim);
      warn('Storage was full — an already-uploaded photo was cleared to make room.');
      await putPhoto(photo);
      await enforceUnsyncedCap();
      return;
    }
    // Nothing safely evictable: keep the punch's photo record but without bytes.
    warn('Storage is full — this photo could not be stored, but the punch was saved.');
    await putPhoto({
      ...photo,
      blob: null,
      status: 'missing',
      missingReason: 'capture_failed',
    });
    return;
  }
  await enforceUnsyncedCap();
}

/**
 * Proactive count cap (complements the reactive `QuotaExceededError` path): while
 * the un-synced backlog exceeds {@link MAX_UNSYNCED_PHOTOS}, drop the oldest
 * ALREADY-uploaded photo. Bounded — stops as soon as nothing is safely evictable,
 * so an un-uploaded photo is never sacrificed to the cap.
 */
async function enforceUnsyncedCap(): Promise<void> {
  let views = await getPhotoViews();
  while (countUnsyncedPhotos(views) > MAX_UNSYNCED_PHOTOS) {
    const victim = selectEvictablePhoto(views);
    if (victim === null) {
      return;
    }
    await deletePhoto(victim);
    views = views.filter((v) => v.idempotencyKey !== victim);
  }
}

/** Apply a partial update to a stored photo by key. */
export async function updatePhoto(
  idempotencyKey: string,
  patch: Partial<QueuedPhoto>
): Promise<void> {
  const existing = await getPhoto(idempotencyKey);
  if (existing === undefined) {
    return;
  }
  await putPhoto({ ...existing, ...patch });
}

/** Delete a queued photo by key. */
export async function deletePhoto(idempotencyKey: string): Promise<void> {
  const db = await openKioskDb();
  await promisify(store(db, 'readwrite').delete(idempotencyKey));
}

async function putPhoto(photo: QueuedPhoto): Promise<void> {
  const db = await openKioskDb();
  await promisify(store(db, 'readwrite').put(photo));
}

function isQuotaExceeded(error: unknown): boolean {
  if (error !== null && typeof error === 'object' && 'name' in error) {
    const { name } = error as { name: unknown };
    return name === 'QuotaExceededError' || name === 'NS_ERROR_DOM_QUOTA_REACHED';
  }
  return false;
}
